import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function AssetFleetExample() {
  useSEO({
    title: 'Asset & Fleet Management Simulation',
    description: 'Simulate maintenance cycles, operational demand, and resource constraints for proactive fleet management.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Asset + Fleet Management</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">Balance utilization and availability across your fleet</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate maintenance cycles, operational demand, and resource constraints to move from reactive scheduling to proactive fleet management.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Fleet Managers</p><p className="text-sm text-gray-600">Balancing utilization with maintenance timing</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Operations Planners</p><p className="text-sm text-gray-600">Meeting rate-of-effort targets under constraints</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Maintenance Directors</p><p className="text-sm text-gray-600">Scheduling inspections without disrupting output</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How to distribute flight hours across aircraft to balance wear</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to pull aircraft for maintenance to minimize backlog</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How calendar-based and hours-based maintenance interact</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">What happens when unmet hours compound over time</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Which utilization policies minimize "hours to catch up"</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between short-term output and long-term availability</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">How it works</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Model your fleet</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Define assets, utilization targets, and maintenance triggers—whether calendar-based, usage-based, or condition-based.</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Simulate scenarios</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Test utilization policies, demand surges, and maintenance timing to see how backlogs accumulate or resolve over time.</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Optimize policies</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Use built-in optimization to find utilization strategies that minimize downtime while meeting operational targets.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Industry examples</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 text-white rounded-lg p-8">
              <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Aviation</p>
              <p className="text-lg leading-relaxed mb-4">A 20-aircraft fleet must meet weekly flight-hour targets while managing dual maintenance regimes—calendar-based inspections and hours-based overhauls. The model identifies utilization policies that eliminate compounding backlogs and increase annual output.</p>
              <p className="text-gray-400 text-sm">The impact: 8% more flight hours with zero accumulated backlog.</p>
            </div>
            <div className="bg-gray-900 text-white rounded-lg p-8">
              <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Trucking + Logistics</p>
              <p className="text-lg leading-relaxed mb-4">A 150-vehicle fleet balances delivery routes against mileage-based service intervals and DOT inspections. Simulation reveals which vehicles to pull from rotation and when—avoiding clustered downtime during peak shipping seasons.</p>
              <p className="text-gray-400 text-sm">The impact: 15% reduction in unplanned breakdowns with stable delivery capacity.</p>
            </div>
            <div className="bg-gray-900 text-white rounded-lg p-8">
              <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Mining + Heavy Equipment</p>
              <p className="text-lg leading-relaxed mb-4">Haul trucks and excavators operate under extreme conditions with run-hour maintenance schedules. The model helps planners see how production targets interact with equipment availability—and where spare capacity should be staged.</p>
              <p className="text-gray-400 text-sm">The impact: 20% improvement in equipment availability during production peaks.</p>
            </div>
          </div>
        </div>

        <HowToEngage
          platformDescription="Model your fleet dynamics, test utilization policies, and run optimization to find strategies that minimize backlog while respecting maintenance constraints."
          consultingDescription="Partner with our operations research team to build custom fleet models, run planning workshops, and develop policies tailored to your maintenance cycles and demand patterns."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/workforce" className="text-gray-700 hover:text-gray-900 text-sm">Capacity + Backlog Planning →</Link>
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">Inventory + Production →</Link>
            <Link to="/solutions/hospital-or" className="text-gray-700 hover:text-gray-900 text-sm">Scheduling + Throughput →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default AssetFleetExample;
